import { useNavigate } from 'react-router';
import { useFormik } from 'formik';
import { useState } from 'react';
import { Application } from '@/api/getClientApplication';
import { useDocumentUpload } from '@/hooks/webapi';
import * as R from '@/Router/routes';
import { SUPPORTED_FORMATS } from '@/utils/constants';
import {
  applyAll,
  invalidFileFormatType,
  restrictFileSize,
} from '@/utils/validations';

type KYCForm = {
  frontSide?: File | null;
  backSide?: File | null;
};

type KYCFormErrors = Partial<Record<keyof KYCForm, string>>;

const validate = (form: KYCForm) => {
  const maxSize = 10 * 1024 * 1024; // 10MB

  const errors: KYCFormErrors = {
    frontSide: applyAll(
      restrictFileSize(maxSize, '10MB'),
      invalidFileFormatType(SUPPORTED_FORMATS),
    )(form.frontSide),

    backSide: applyAll(
      restrictFileSize(maxSize, '10MB'),
      invalidFileFormatType(SUPPORTED_FORMATS),
    )(form.backSide),
  };

  const hasAnyError = Object.values(errors).filter(Boolean).length > 0;

  return hasAnyError ? errors : {};
};

export const shouldNavigateToUploadKyc = (
  application: Application | null | undefined,
) =>
  application?.resolution === 'MANUAL' &&
  application?.resolutionDetail === 'EXPIRED_IDENTITY_DOCUMENT';

export const useUploadKYC = () => {
  const navigate = useNavigate();
  const [pending, setPending] = useState(false);
  const { documentUpload } = useDocumentUpload();

  const handleSubmit = async (values: KYCForm) => {
    try {
      setPending(true);

      if (values.frontSide) {
        await documentUpload(values.frontSide, 'frontSideDni');
      }
      if (values.backSide) {
        await documentUpload(values.backSide, 'backSideDni');
      }

      navigate(R.KYC_UPLOAD_PENDING);
    } catch (error) {
      setPending(false);
    } finally {
      setPending(false);
    }
  };

  const formik = useFormik<KYCForm>({
    initialValues: {
      frontSide: null,
      backSide: null,
    },
    onSubmit: handleSubmit,
    validate,
  });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name } = event.target;
    const file = event.target.files ? event.target.files[0] : null;

    formik.setFieldTouched(name, true);
    formik.setFieldValue(name, file);
  };

  const handleDelete = (name: string) => {
    formik.setFieldValue(name, null);
  };

  const isDisabled = !(
    Boolean(formik.values.frontSide) && Boolean(formik.values.backSide)
  );

  return {
    isDisabled,
    handleDelete,
    handleChange,
    formik,
    pending,
  };
};
